import React, { useState, useMemo, useContext } from 'react';
import { PropTypes } from 'prop-types';

import OddConverter from '@common/helpers/oddConverter';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';
import { BetCalculationsEnum, getBetCalculations } from '@betslip/calculation';

import MoneyAmount from '@common/components/moneyAmount';
import Loader from '@common/components/loader';
import NumericInput from '@common/components/numericInput';
import { useTranslation } from '@common/hooks/translation';
import { broadcast, broadcastChannels } from '@common/helpers/broadcast';
import { PreferencesContext } from '@common/contexts/preferences';

import ToastWrapper from '@common/helpers/toastWrapper';
import { changeTicketResultModel, generatePredefinedMultipleBonusRule, createPredefinedBet } from '../service';

const MultiplesItemPlaceBet = ({
  balance, config, partnerConfig, currencyConfig, events, multiple,
}) => {
  const t = useTranslation();
  const [stake, setStake] = useState();
  const [loading, setLoading] = useState();
  const [errorMsg, setErrorMsg] = useState();
  const [{ oddsFormat }] = useContext(PreferencesContext);

  const getSelections = () => multiple.Selections.map((item) => ({
    SelectionId: item.SelectionId,
    Price: events?.find((event) => event.id === item.SelectionId)?.price,
  }));
  const bonusRule = useMemo(() => generatePredefinedMultipleBonusRule(multiple), [multiple]);

  const {
    possibleWin, taxedStake, expressBonus, totalOdd, totalWin, profitOrReturnTax,
  } = getBetCalculations(
    [
      BetCalculationsEnum.Bonus,
      BetCalculationsEnum.PossibleWin,
      BetCalculationsEnum.TaxOnStake,
      BetCalculationsEnum.TotalOdd,
      BetCalculationsEnum.TaxForReturnOrProfit,
      BetCalculationsEnum.TotalWin,
    ],
    {
      config: { ...config.betslip, odd: config.odd, enableExpressBonus: true },
      stake: +stake,
      events: events?.map((item) => ({
        game: item.game,
        market: item.market,
        event: item,
      })),
      partnerConfig,
      bonusRule: [bonusRule],
      currencyConfig,
    },
  );

  const totalStake = taxedStake; // by now

  // const getMaxStake = async () => {
  //   const params = {
  //     selection_list: multiple.Selections.map(item => ({ SelectionId: item.SelectionId })),
  //     type_id: multiple.BetType,
  //     token,
  //   };

  //   const {
  //      data: { MaxStake: maxStake } = {}
  //   } = await swarmRequest(params, 'terminal_get_bet_max_stake');
  //   maxStake && setStake(maxStake);
  // }

  const placeBet = async () => {
    setLoading(true);
    setErrorMsg('');

    const data = await swarmRequest(
      createPredefinedBet(stake, multiple.Id, getSelections()),
      'betshop_command',
    );

    if (data.data?.Message !== 'Bet Type Error') setErrorMsg(data.data?.Message);
    if (data.data?.Message === 'Bet Type Error') ToastWrapper('error', t(`${data.data?.Message}`));
    else {
      const ticketResult = changeTicketResultModel(data.data[0]);

      broadcast.dispatchEvent(
        new CustomEvent(broadcastChannels.PrintTicket, {
          detail: {
            ...ticketResult,
            price: OddConverter(config, ticketResult.price, oddsFormat),
            selection_list: ticketResult.selection_list.map((item) => ({
              ...item,
              price: OddConverter(config, item.price, oddsFormat),
            })),
          },
        }),
      );
    }

    setLoading(false);
    setStake();
  };

  const isPlaceBetDisabled = !stake || +stake > balance;

  return (
    <div className="place-bet-container">
      <div className="place-bet-container-wrp">
        <NumericInput
          className="multiple"
          onChange={setStake}
          value={stake}
          placeholder={t('Stake...')}
          maxLength={20}
          maxDecimalCount={currencyConfig?.rounding || 2}
        />
        <div className="bottom-block">
          {/*
            <div>{t("Multiple:")} <span>{events?.length}</span></div>
          */}
          {!!totalOdd && (
            <div className="multiple-odds">
              <p className="multiple-odds-txt">{t('Odds')}</p>
              <span className="multiple-odds-number">
                {OddConverter(config, totalOdd, oddsFormat)}
              </span>
            </div>
          )}

          {!!config.betslip.taxPercentOnStake && !!taxedStake && (
            <div className="multiple-odds">
              <p className="m-acc-txt">
                {t('Tax on stake')}
                :
              </p>
              <p className="m-acc-percent">
                {config.betslip.taxPercentOnStake}
                %
              </p>
              <span className="multiple-odds-number">
                <MoneyAmount
                  config={config}
                  currencyConfig={currencyConfig}
                  amount={+stake - taxedStake}
                />
              </span>
            </div>
          )}
          {!!totalStake && (
            <div className="multiple-odds">
              <p className="multiple-odds-txt">{t('Total stake:')}</p>
              <span className="multiple-odds-number">
                <MoneyAmount
                  config={config}
                  currencyConfig={currencyConfig}
                  amount={totalStake}
                />
              </span>
            </div>
          )}

          {!!possibleWin && config.main.fieldsToHide.enableTotalWinField && (
            <div className="multiple-odds">
              <p className="multiple-odds-txt">{t('Possible win:')}</p>
              <span className="multiple-odds-number">
                <MoneyAmount
                  config={config}
                  currencyConfig={currencyConfig}
                  amount={possibleWin}
                />
              </span>
            </div>
          )}

          {!!expressBonus?.percent && (
            <div className="multiple-odds">
              <p className="m-acc-txt">{t('Bonus:')}</p>
              <p className="m-acc-percent">
                {expressBonus.percent}
                %
              </p>
              <span className="multiple-odds-number">
                <MoneyAmount
                  config={config}
                  currencyConfig={currencyConfig}
                  amount={expressBonus.amount || 0}
                />
              </span>
            </div>
          )}
        </div>
        {!!profitOrReturnTax && (
          <div className="multiple-odds">
            <p className="multiple-odds-txt">{t('Tax on Return:')}</p>
            <span className="multiple-odds-number">
              <MoneyAmount
                config={config}
                currencyConfig={currencyConfig}
                amount={profitOrReturnTax}
              />
            </span>
          </div>
        )}
        {!!totalWin && (
          <div className="multiple-odds">
            <p className="multiple-odds-txt">{t('Total Win:')}</p>
            <span className="multiple-odds-number">
              <MoneyAmount
                config={config}
                currencyConfig={currencyConfig}
                amount={totalWin}
              />
            </span>
          </div>
        )}
        {errorMsg && <p className="error-msg">{errorMsg}</p>}

        {config.main.fieldsToHide.enablePlaceBet && (
        <div className="separator-box-buttons">
          <button
            type="button"
            className="button-view-normal"
            disabled={isPlaceBetDisabled}
            onClick={placeBet}
          >
            {t('Place bet')}
          </button>
        </div>
        )}

        {loading && <Loader />}
      </div>
    </div>
  );
};

MultiplesItemPlaceBet.propTypes = {
  events: PropTypes.array,
  multiple: PropTypes.object,
  balance: PropTypes.number,
  config: PropTypes.object,
  partnerConfig: PropTypes.object,
  currencyConfig: PropTypes.object,
};

export default MultiplesItemPlaceBet;
